class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.onTummyHushCartClick.bind(this), true);
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initScrollHint();
  }

  connectedCallback() {
    this.visitTummyHushAB();
    this.updateTummyHushContext();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.updateTummyHushContext());
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
  }

  updateTummyHushContext() {
    this.applyTummyHushAB();
    const pageContext = this.dataset.thPageCtx;
    if (pageContext === 'dd' || pageContext === 'band') {
      this.dataset.thCtx = pageContext;
      return;
    }
    const inner = this.querySelector('.th-cart__inner');
    if (!inner) return;
    this.dataset.thCtx = Number(inner.dataset.thDdUnits) > 0 && Number(inner.dataset.thBandUnits) === 0 ? 'dd' : 'band';
  }

  applyTummyHushAB() {
    const state = CartDrawer.thABState;
    const bucket = this.dataset.thAbEnabled === 'true' && state && state.bucket;
    if (bucket) this.dataset.thAb = bucket;
    else delete this.dataset.thAb;
    this.querySelectorAll('[data-th-ab-b]').forEach((block) => { block.hidden = bucket !== 'B'; });
    // 2026-09-28 three-way test: [data-th-ab-show="B X"] lists the buckets that see a block.
    this.querySelectorAll('[data-th-ab-show]').forEach((block) => {
      block.hidden = !(bucket && block.dataset.thAbShow.split(' ').includes(bucket));
    });
    this.querySelectorAll('.th-cart__ab-recommendations[data-th-ab-show]').forEach((section) => {
      if (!section.hidden && !section.querySelector('.th-cart__ab-card:not([hidden])')) section.hidden = true;
    });
  }

  // 2026-10-02: the arm is decided on the first page view, not the first drawer open, so revenue can be read per visitor.
  // th_cart_ab_visit fires once per session for every visitor; th_cart_ab (cart openers) and the Meta event are unchanged.
  visitTummyHushAB() {
    if (this.dataset.thAbEnabled !== 'true') return;
    const state = this.resolveTummyHushAB();
    if (state.visitLogged) return;
    state.visitLogged = true;
    try { if (typeof gtag === 'function') gtag('set', 'user_properties', { ab_bucket: state.bucket }); } catch (error) { /* Optional analytics. */ }
    let seen = false;
    try { seen = sessionStorage.getItem('th_cart_ab_visit_s' + (state.version || '1')) === '1'; } catch (error) { /* Optional analytics. */ }
    if (seen) return;
    try { sessionStorage.setItem('th_cart_ab_visit_s' + (state.version || '1'), '1'); } catch (error) { /* Memory still guards this page. */ }
    try { if (typeof gtag === 'function') gtag('event', 'th_cart_ab_visit', { ab_bucket: state.bucket }); } catch (error) { /* Optional analytics. */ }
  }

  resolveTummyHushAB() {
    const state = CartDrawer.thABState || (CartDrawer.thABState = {});
    if (!state.bucket) {
      // X = "A2": control drawer + the DreamDots cross-sell card only. A three-way test re-randomises everyone under a new key.
      const splitX = Math.max(0, Math.min(100, Number(this.dataset.thAbSplitX) || 0));
      const threeWay = splitX > 0;
      // 2026-10-15 relaunch A vs A2: fresh key + version 3 so every visitor is re-randomised 50/50.
      const storageKey = threeWay ? 'th_cart_ab4' : 'th_cart_ab';
      const split = Math.max(0, Math.min(100, Number(this.dataset.thAbSplit)));
      const shareB = Number.isFinite(split) ? split : 50;
      // 2026-10-02: an arm set to 0% is retired; visitors stored in it are re-randomised into the live arms.
      const assignable = (threeWay ? ['A', 'B', 'X'] : ['A', 'B']).filter((arm) => arm !== 'B' || shareB > 0);
      let stored;
      try { stored = localStorage.getItem(storageKey); } catch (error) { /* Storage may be blocked. */ }
      if (!assignable.includes(stored)) {
        const roll = Math.random() * 100;
        stored = roll < splitX ? 'X' : roll < splitX + shareB ? 'B' : 'A';
        try { localStorage.setItem(storageKey, stored); } catch (error) { /* Keep the in-memory bucket. */ }
      }
      const overrides = ['A', 'B', 'X'];
      const forced = this.dataset.thAbForce;
      const query = new URLSearchParams(window.location.search).get('th_ab');
      state.bucket = overrides.includes(forced) ? forced : overrides.includes(query) ? query : stored;
      state.version = threeWay ? '3' : '1';
    }
    return state;
  }

  openTummyHushAB() {
    if (this.dataset.thAbEnabled !== 'true') return;
    const state = this.resolveTummyHushAB();
    this.applyTummyHushAB();
    this.stampTummyHushAB(state);
    if (!state.exposureAttempted) {
      state.exposureAttempted = true;
      // User property: the bucket then rides every later GA4 hit, so any report can be split by it.
      try { if (typeof gtag === 'function') gtag('set', 'user_properties', { ab_bucket: state.bucket }); } catch (error) { /* Optional analytics. */ }
      let sessionSeen = false;
      try { sessionSeen = sessionStorage.getItem('th_cart_ab_exposed_s' + (state.version || '1')) === '1'; } catch (error) { /* Optional analytics. */ }
      if (!sessionSeen) {
        try { sessionStorage.setItem('th_cart_ab_exposed_s' + (state.version || '1'), '1'); } catch (error) { /* Memory still guards this page. */ }
        try { if (typeof gtag === 'function') gtag('event', 'th_cart_ab', { ab_bucket: state.bucket }); } catch (error) { /* Optional analytics. */ }
      }
      let exposed = false;
      try { exposed = localStorage.getItem('th_cart_ab_exposed_v' + (state.version || '1')) === '1'; } catch (error) { /* Optional analytics. */ }
      if (!exposed) {
        try { localStorage.setItem('th_cart_ab_exposed_v' + (state.version || '1'), '1'); } catch (error) { /* Memory still guards this page. */ }
        try { if (typeof fbq === 'function') fbq('trackCustom', 'ThCartAB', { bucket: state.bucket }); } catch (error) { /* Optional analytics. */ }
      }
    }
  }

  async stampTummyHushAB(state) {
    if (state.stampAttempted) return;
    // Set before the GET: opens, section replacements and ebook-sync cannot start another attempt.
    state.stampAttempted = true;
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      const version = state.version || '1';
      const attrs = cart.attributes || {};
      if ((attrs.th_cart_ab === state.bucket && (attrs.th_cart_ab_v || '1') === version) || this.dataset.thAbEnabled !== 'true') return;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { th_cart_ab: state.bucket, th_cart_ab_v: state.version || '1' } })
      });
    } catch (error) { /* Attribution must never block shopping or retry in an ebook-sync loop. */ }
  }

  async addTummyHushAB(button) {
    if (button.disabled || this.thABAdding || !['B', 'X'].includes(this.dataset.thAb) || this.dataset.thAbEnabled !== 'true') return;
    this.thABAdding = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const spinner = button.querySelector('.th-cart__ab-spinner');
    if (spinner) spinner.hidden = false;
    this.showTummyHushCartError('');
    let added = false;
    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(button.dataset.thVariantId), quantity: 1 })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || window.cartStrings?.error);
      added = true;
      await this.querySelector('cart-drawer-items').refreshTummyHushABCart();
    } catch (error) {
      this.showTummyHushCartError(error.message);
    } finally {
      this.thABAdding = false;
      // If only the section fetch failed, keep this stale CTA disabled to avoid adding twice.
      if (!added) button.disabled = false;
      button.removeAttribute('aria-busy');
      if (spinner) spinner.hidden = true;
    }
  }

  onTummyHushABClick(event) {
    const control = event.target.closest?.('[data-th-ab-swatch], [data-th-ab-size], [data-th-ab-add]');
    if (!control) return false;
    event.preventDefault();
    event.stopPropagation();
    if (!['B', 'X'].includes(this.dataset.thAb) || this.dataset.thAbEnabled !== 'true' || control.disabled || this.thABAdding) return true;
    if (control.hasAttribute('data-th-ab-swatch') || control.hasAttribute('data-th-ab-size')) {
      const picker = control.closest('[data-th-ab-picker], [data-th-ab-ddpick]');
      const group = control.hasAttribute('data-th-ab-size') ? '[data-th-ab-size]' : '[data-th-ab-swatch]';
      picker.querySelectorAll(group).forEach((option) => {
        const active = option === control;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      const button = picker.querySelector('[data-th-ab-add]');
      if (button.dataset.thVariants) {
        // Colour + size pick a variant from the rendered map; never guess a size for the shopper.
        let map = {};
        try { map = JSON.parse(button.dataset.thVariants); } catch (error) { map = {}; }
        const colour = picker.querySelector('[data-th-ab-swatch].is-active')?.dataset.thColour;
        const size = picker.querySelector('[data-th-ab-size].is-active')?.dataset.thSize;
        const match = map[`${colour}|${size}`];
        button.dataset.thVariantId = match ? String(match.id) : '';
        button.disabled = !match || match.a !== true;
      } else {
        button.dataset.thVariantId = control.dataset.thVariantId;
        button.disabled = control.dataset.thAbAvailable !== 'true';
      }
    } else {
      this.addTummyHushAB(control);
    }
    return true;
  }

  /* 2026-09-04: pulsing "more below" arrow above the checkout box while the item list can still scroll. */
  initScrollHint() {
    const update = () => this.updateScrollHint();
    this.addEventListener('scroll', update, true);
    this.addEventListener('click', (event) => {
      const hint = event.target.closest?.('[data-th-scroll-hint]');
      if (!hint) return;
      event.preventDefault();
      const body = this.querySelector('.th-cart__body');
      if (body) body.scrollBy({ top: Math.max(body.clientHeight * 0.7, 160), behavior: 'smooth' });
    });
    window.addEventListener('resize', update);
    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(() => { setTimeout(update, 60); setTimeout(update, 400); }).observe(this, { childList: true, subtree: true });
    }
    setTimeout(update, 300);
  }

  updateScrollHint() {
    const hint = this.querySelector('[data-th-scroll-hint]');
    const body = this.querySelector('.th-cart__body');
    if (!hint || !body) return;
    const remaining = body.scrollHeight - body.clientHeight - body.scrollTop;
    hint.hidden = !(this.classList.contains('active') && remaining > 28);
  }

  showTummyHushCartError(message) {
    const errorNode = this.querySelector('#CartDrawer-CartErrors');
    if (errorNode) {
      errorNode.textContent = message === ''
        ? ''
        : message || window.cartStrings?.error || 'Something went wrong. Please try again.';
    }
  }

  async addTummyHushGift(button) {
    if (button.disabled) return;
    const originalText = button.textContent;
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Adding…';
    this.showTummyHushCartError('');

    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: Number(button.dataset.thVariantId), quantity: 1 })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || window.cartStrings?.error);

      const drawerItems = this.querySelector('cart-drawer-items');
      if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
        drawerItems.onCartUpdate();
      } else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'th-gift' });
      }
    } catch (error) {
      this.showTummyHushCartError(error.message);
      button.disabled = false;
      button.classList.remove('is-loading');
      button.textContent = originalText;
    }
  }

  onTummyHushCartClick(event) {
    if (this.onTummyHushABClick(event)) return;
    const giftSwatch = event.target.closest?.('[data-th-gift-swatch]');
    if (giftSwatch) {
      event.preventDefault();
      event.stopPropagation();
      const picker = giftSwatch.closest('[data-th-gift-picker]');
      picker?.querySelectorAll('[data-th-gift-swatch]').forEach((swatch) => {
        const active = swatch === giftSwatch;
        swatch.classList.toggle('is-active', active);
        swatch.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      const addButton = picker?.querySelector('[data-th-gift-add]');
      if (addButton) {
        addButton.dataset.thVariantId = giftSwatch.dataset.thVariantId;
        addButton.dataset.thColour = giftSwatch.dataset.thColour;
        const product = picker.dataset.thGiftProduct || 'band';
        const size = picker.dataset.thGiftSize;
        addButton.textContent = `Add my free ${giftSwatch.dataset.thColour} ${product}${size ? ` · ${size}` : ''}`;
      }
      return;
    }

    const giftAdd = event.target.closest?.('[data-th-gift-add]');
    if (giftAdd) {
      event.preventDefault();
      event.stopPropagation();
      this.addTummyHushGift(giftAdd);
      return;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble') || document.querySelector('a[aria-label="Cart"], a[href="/cart"]');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.openTummyHushAB();
    this.cancelPendingClose();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active'); setTimeout(() => this.updateScrollHint(), 450);});
    if (this.scrollHintTimer) clearInterval(this.scrollHintTimer);
    this.scrollHintTimer = setInterval(() => this.updateScrollHint(), 500);

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    this.lockPageScroll();
  }

  /* 2026-09-04 iOS Safari: body{overflow:hidden} does not stop the document from scrolling on iOS, and the
     document then steals touch scrolls from the drawer body. Standard fix: fix the body in place at the
     current scroll offset while the drawer is open, restore on close. */
  lockPageScroll() {
    if (this.pageScrollLocked) return;
    const y = window.pageYOffset || document.documentElement.scrollTop || 0;
    this.pageScrollY = y;
    this.pageScrollLocked = true;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  unlockPageScroll() {
    if (!this.pageScrollLocked) return;
    this.pageScrollLocked = false;
    const y = this.pageScrollY || 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, y);
    document.documentElement.style.scrollBehavior = prev;
  }

  close() {
    this.classList.remove('active');

    const panel = this.querySelector('#CartDrawer');
    const finishClose = () => {
      if (this.closeAnimationTimeout) clearTimeout(this.closeAnimationTimeout);
      if (panel && this.closeTransitionHandler) {
        panel.removeEventListener('transitionend', this.closeTransitionHandler);
      }
      this.closeAnimationTimeout = null;
      this.closeTransitionHandler = null;
      this.closeTransitionPanel = null;
      if (!this.classList.contains('active')) this.classList.remove('animate');
    };

    this.cancelPendingClose();
    if (panel) {
      this.closeTransitionPanel = panel;
      this.closeTransitionHandler = finishClose;
      panel.addEventListener('transitionend', this.closeTransitionHandler, { once: true });
    }
    this.closeAnimationTimeout = setTimeout(finishClose, 450);

    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.unlockPageScroll();
    if (this.scrollHintTimer) { clearInterval(this.scrollHintTimer); this.scrollHintTimer = null; }
    const hint = this.querySelector('[data-th-scroll-hint]');
    if (hint) hint.hidden = true;
  }

  cancelPendingClose() {
    if (this.closeAnimationTimeout) clearTimeout(this.closeAnimationTimeout);
    if (this.closeTransitionPanel && this.closeTransitionHandler) {
      this.closeTransitionPanel.removeEventListener('transitionend', this.closeTransitionHandler);
    }
    this.closeAnimationTimeout = null;
    this.closeTransitionHandler = null;
    this.closeTransitionPanel = null;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const sectionHTML = parsedState.sections && parsedState.sections[section.id];
      if (!sectionElement || !sectionHTML) return;
      const innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      if (innerHTML != null) sectionElement.innerHTML = innerHTML;
    }));
    this.updateTummyHushContext();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      const checkoutButton = this.querySelector('[name="checkout"], #CartDrawer-Checkout');
      if (checkoutButton && !document.getElementById(checkoutButton.getAttribute('form') || 'CartDrawer-Form')) {
        checkoutButton.addEventListener('click', (event) => {
          event.preventDefault();
          window.location.href = '/checkout';
        });
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    fetch('/?section_id=cart-drawer')
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceDrawer = html.querySelector('#CartDrawer');
        const currentDrawer = document.querySelector('#CartDrawer');
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (sourceDrawer && currentDrawer) currentDrawer.innerHTML = sourceDrawer.innerHTML;
        cartDrawerWrapper?.updateTummyHushContext();
        if (cartDrawerWrapper) cartDrawerWrapper.classList.remove('is-empty');
        document.querySelector('.drawer__inner')?.classList.remove('is-empty');
        document.querySelector('cart-drawer-items')?.classList.remove('is-empty');
      })
      .catch(e => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
